/**
 * pc-behavior - passive behavior collector.
 *
 * Accumulates element visibility/clicks (data-pc-obs tagged), friction
 * signals (rage clicks, dead clicks, no-fit result), and scroll depth for the
 * current pageview, then ships ONE `behavior` event through Relay when the
 * page is hidden. Load AFTER relay.js in layout/theme.liquid.
 *
 * Fails soft everywhere: analytics must never break the storefront.
 */
(function (window, document) {
  'use strict';

  if (window.PCBehavior && window.PCBehavior.__loaded) { return; }

  var state = {
    template: window.__pcTemplate || 'unknown',
    seen: {}, clicked: {}, fold: {},
    scroll: 0, rage: {}, dead: 0, nofit: false, sent: false
  };

  function keys(obj) {
    var out = [], k;
    for (k in obj) { if (obj.hasOwnProperty(k)) { out.push(k); } }
    return out;
  }

  function vals(obj) {
    var out = [], k;
    for (k in obj) { if (obj.hasOwnProperty(k)) { out.push(obj[k]); } }
    return out;
  }

  // --- element visibility + above-the-fold ---------------------------------
  // Above-the-fold is read from each element's FIRST IntersectionObserver
  // entry (geometry computed off the critical path) rather than calling
  // getBoundingClientRect() per element, which forced a synchronous layout of
  // the whole page before first paint.
  function initObserved() {
    var els = document.querySelectorAll('[data-pc-obs]');
    var vh = window.innerHeight || 0;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        var key = el.getAttribute('data-pc-obs');
        try {
          if (el.getBoundingClientRect().top < vh) { state.fold[key] = 1; }
        } catch (e) {}
        state.seen[key] = 1;
      });
      return;
    }
    var first = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var key = en.target.getAttribute('data-pc-obs');
        if (first.indexOf(en.target) === -1) {
          first.push(en.target);
          var rootH = en.rootBounds ? en.rootBounds.height : vh;
          if (en.boundingClientRect.top < rootH) { state.fold[key] = 1; }
        }
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          state.seen[key] = 1;
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  // --- clicks: tagged elements, rage, dead ---------------------------------
  var lastClicks = [];   // [{sel, t}]

  // One node: tag, plus an id or first class when it has one. A bare tag (li, p,
  // td) names nothing on its own, so it gets its position among its siblings.
  function nodePart(el, positional) {
    var s = el.tagName ? el.tagName.toLowerCase() : '?';
    if (el.id) { return s + '#' + el.id; }
    if (el.classList && el.classList.length) { return s + '.' + el.classList[0]; }
    if (!positional) { return s; }
    var i = 1, sib = el;
    while ((sib = sib.previousElementSibling)) {
      if (sib.tagName === el.tagName) { i++; }
    }
    return s + ':nth-of-type(' + i + ')';
  }

  // Ancestor path down to the clicked node, up to 3 hops, stopping at the first
  // element with an id or a data-pc-obs tag - that anchors the path to something
  // findable and keeps clicks inside one region grouped under a common prefix.
  function pathFor(el) {
    var parts = [nodePart(el, true)];
    var node = el.parentElement, hops = 0;
    while (node && node !== document.body && hops < 3) {
      var anchored = !!node.id || (node.hasAttribute && node.hasAttribute('data-pc-obs'));
      parts.unshift(nodePart(node, false));
      if (anchored) { break; }
      node = node.parentElement;
      hops++;
    }
    return parts.join('>').slice(0, 100);
  }

  // What the element says, so a path can be recognised without opening devtools.
  function labelFor(el) {
    var t = '';
    try {
      t = (el.tagName === 'IMG')
        ? (el.getAttribute('alt') || el.getAttribute('title') || '')
        : (el.textContent || '');
      t = t.replace(/\s+/g, ' ').trim();
    } catch (e) { return ''; }
    if (t === '') { return ''; }
    // Audit TH-5: a label is for recognising a button, never for carrying
    // what a shopper typed or was shown - addresses, emails, order numbers.
    t = t.replace(/[^\s@]+@[^\s@]+/g, '[email]').replace(/\d{5,}/g, '#');
    return t.length > 40 ? t.slice(0, 40) + '…' : t;
  }
  function isInteractive(el) {
    while (el && el !== document.body) {
      if (el.hasAttribute && (el.hasAttribute('data-pc-obs') || el.hasAttribute('onclick'))) { return true; }
      var tag = el.tagName;
      if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' ||
          tag === 'TEXTAREA' || tag === 'LABEL' || tag === 'SUMMARY' || tag === 'VIDEO') { return true; }
      if (el.getAttribute && el.getAttribute('role') === 'button') { return true; }
      el = el.parentElement;
    }
    return false;
  }
  function onPointerDown(ev) {
    var el = ev.target;
    if (!el || !el.closest) { return; }

    var tagged = el.closest('[data-pc-obs]');
    if (tagged) { state.clicked[tagged.getAttribute('data-pc-obs')] = 1; }

    var now = Date.now();
    // Rapid-repeat detection runs on the path alone. Text can change between
    // clicks ("Add to cart" becoming "Added"), and a key that changed mid-burst
    // would never reach three.
    var sel = pathFor(el);
    lastClicks.push({ sel: sel, t: now });
    if (lastClicks.length > 6) { lastClicks.shift(); }
    var rapid = lastClicks.filter(function (c) { return c.sel === sel && now - c.t < 1000; });
    if (rapid.length >= 3) {
      var text = labelFor(el);
      state.rage[sel] = sel + (text ? ' "' + text + '"' : '');
    }

    if (!tagged && !isInteractive(el)) { state.dead++; }
  }

  // --- scroll milestones ---------------------------------------------------
  function onScroll() {
    try {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) { state.scroll = 100; return; }
      var pct = Math.round((window.pageYOffset / max) * 100);
      [25, 50, 75, 100].forEach(function (m) {
        if (pct >= m && state.scroll < m) { state.scroll = m; }
      });
    } catch (e) {}
  }

  // --- no-fit flag: wrap Relay.track once ----------------------------------
  function hookFitment() {
    if (!(window.Relay && window.Relay.track)) { return; }
    var orig = window.Relay.track;
    window.Relay.track = function (name, props) {
      try {
        if (name === 'fitment_checked' && props && props.result === 'does_not_fit') {
          state.nofit = true;
        }
      } catch (e) {}
      return orig.apply(window.Relay, arguments);
    };
  }

  // --- send once on hide ---------------------------------------------------
  function send() {
    if (state.sent || !(window.Relay && window.Relay.track)) { return; }
    state.sent = true;
    window.Relay.track('behavior', {
      template: state.template,
      seen: keys(state.seen),
      clicked: keys(state.clicked),
      fold: keys(state.fold),
      scroll: state.scroll,
      rage: vals(state.rage).slice(0, 5),
      dead: state.dead,
      nofit: state.nofit
    });
    window.Relay.flush(true);   // beacon; relay.js's own pagehide flush is a backstop
  }

  // Work that measures layout waits until after the page has painted, so it
  // never forces a layout ahead of first paint.
  function afterPaint(fn) {
    var run = function () {
      if ('requestIdleCallback' in window) { window.requestIdleCallback(fn, { timeout: 2000 }); }
      else { setTimeout(fn, 1); }
    };
    if (document.readyState === 'complete') { run(); }
    else { window.addEventListener('load', run); }
  }

  function boot() {
    hookFitment();
    document.addEventListener('pointerdown', onPointerDown, { passive: true, capture: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    afterPaint(function () { initObserved(); onScroll(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { send(); }
    });
    window.addEventListener('pagehide', send);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.PCBehavior = { __loaded: true };
})(window, document);
